import { ClerkFailed, ClerkLoading, Show } from '@clerk/react-router';
import { createActorContext } from '@xstate/react';
import { Route, Routes, useMatch } from 'react-router';
import { createActor } from 'xstate';
import { paths } from '~/routing/routes';
import { PublicHeader } from './components';
import { ConfirmationDialog } from './components/ConfirmationDialog';
import { SiteFooter } from './components/footers/SiteFooter';
import { PublicScrollbarGutter } from './components/PublicScrollbarGutter';
import { BrandSync } from './config/BrandSync';
import Alert from './features/alerts/Alert';
import { alertView } from './features/alerts/alertView.machine';
import { confirmationView } from './features/confirmation/confirmationView.machine';
import { FeatureFlagProvider } from './features/feature-flags/FeatureFlagProvider';
import { PeerReference } from './features/peerReference/PeerReference';
import { userStateV2 } from './features/user/userState.v2.machine';
import { USER_STATE } from './features/xstateSystem';
import './fonts';
import { ObservabilityIdentityBridge } from './observability/useObservabilityIdentity';
import {
  AboutUs,
  AuthLoading,
  AuthUnavailable,
  ContactUs,
  EmployerSignUpPage,
  EmployerUserInvitationVerify,
  Eula,
  FeaturesPage,
  ForgotPasswordPage,
  LandingPage,
  LoginOrCreateAccountPage,
  Organizations,
  Privacy,
  Providers,
  ProviderSignUpPage,
  Resources,
  ScheduleA,
  ScheduleB,
  SecurityPage,
  SessionTaskPage,
  SignInPage,
  SignUpPage,
  SSOCallbackPage,
  Terms,
} from './pages';
import { ProviderAssociationVerify } from './pages/ProviderAssociationVerify';
import { AuthCompletePage } from './routing/AuthCompletePage';
import { AuthenticatedRoutes } from './routing/AuthenticatedRoutes';
import { AuthStateMachine } from './routing/AuthStateHook';
import { ProviderNameHintSync } from './routing/ProviderNameHintSync';
import { ScrollToTop } from './routing/ScrollToTop';
import { SignedInShell } from './routing/SignedInShell';
import { UnauthenticatedFallback } from './routing/UnauthenticatedFallback';
import './styles.css';
import { TokenGetter } from './utils/auth';

export const UserContext = createActorContext(userStateV2, {
  id: USER_STATE,
  systemId: USER_STATE,
});
// Temporary compatibility alias for migrating from the old UserContextV2 name; remove once all references are updated to use UserContext
export const UserContextV2 = UserContext;

export const useAlertView = () => {
  const userActorRef = UserContextV2.useActorRef();
  const snapshot = userActorRef.getSnapshot();

  // Access via context (spawned in context initializer)
  const alertRef = snapshot.context.alertRef as
    | ReturnType<typeof createActor<typeof alertView>>
    | undefined;

  if (!alertRef) {
    throw new Error(
      'alertView not found in userState context. Make sure userState has spawned it.'
    );
  }

  return alertRef;
};

export const useConfirmationView = () => {
  const userActorRef = UserContextV2.useActorRef();
  const snapshot = userActorRef.getSnapshot();

  // Access via context (spawned in context initializer)
  const confirmationRef = snapshot.context.confirmationRef as
    | ReturnType<typeof createActor<typeof confirmationView>>
    | undefined;

  if (!confirmationRef) {
    throw new Error(
      'confirmationView not found in userState system. Make sure userState has spawned it.'
    );
  }

  return confirmationRef;
};

const App = () => {
  const isPeerReferenceRoute = Boolean(useMatch('/peerReference/:magicLink'));

  return (
    <>
      <div className="bg-base text-content-primary min-h-screen">
        <UserContext.Provider>
          <BrandSync />
          <ProviderNameHintSync />
          {/* TokenGetter must mount before AuthStateMachine so the axios
              token singleton is wired up before the state machine kicks
              off the post-login user fetch. Otherwise the first
              `fetchUser` after Clerk's `isSignedIn` flips can race the
              TokenGetter useEffect, fire with a null bearer token, and
              hang the dashboard until the user refreshes.

              Both stay mounted wherever the persistent user actor is, the
              peer reference page included: an external sign-out there must
              still end the session of the actors the actor holds. */}
          <TokenGetter />
          <AuthStateMachine />
          {isPeerReferenceRoute ? (
            <Routes>
              <Route path="/peerReference/:magicLink" element={<PeerReference />} />
            </Routes>
          ) : (
            <>
              <Alert />

              <ConfirmationDialog />
              {/* Show renders null for both auth branches while clerk-js is
                  hydrating. Keep the transition visible, and make a Clerk
                  script failure recoverable instead of showing the base-grey
                  application shell indefinitely. */}
              <ClerkLoading>
                <AuthLoading />
              </ClerkLoading>
              <ClerkFailed>
                <AuthUnavailable />
              </ClerkFailed>
              <Show when="signed-out">
                <PublicScrollbarGutter />
                <ScrollToTop />
                <div className="flex flex-col min-h-screen pt-20">
                  <PublicHeader />
                  <Routes>
                    <Route path="/" element={<LandingPage />} />
                    <Route path={paths.publicAuth.authComplete} element={<AuthCompletePage />} />
                    <Route path={`${paths.publicAuth.signIn}/*`} element={<SignInPage />} />
                    <Route path={`${paths.publicAuth.signUp}/*`} element={<SignUpPage />} />
                    <Route
                      path={paths.providerAssociation.verify}
                      element={<ProviderAssociationVerify />}
                    />
                    <Route path={`${paths.publicAuth.signInLegacy}/*`} element={<SignInPage />} />
                    <Route
                      path={`${paths.publicAuth.signUpProvider}/*`}
                      element={<ProviderSignUpPage />}
                    />
                    <Route
                      path={`${paths.publicAuth.signUpEmployer}/*`}
                      element={<EmployerSignUpPage />}
                    />
                    <Route
                      path={paths.employerUserInvitation.verify}
                      element={<EmployerUserInvitationVerify />}
                    />
                    <Route path="/signUp" element={<LoginOrCreateAccountPage />} />
                    <Route
                      path={paths.publicAuth.loginOrCreateAccount}
                      element={<LoginOrCreateAccountPage />}
                    />
                    <Route path="/about" element={<AboutUs />} />
                    <Route path="/contact" element={<ContactUs />} />
                    <Route path="/features" element={<FeaturesPage />} />
                    <Route path="/faq" element={<Resources />} />
                    <Route path="/resources" element={<Resources />} />
                    <Route path="/organizations" element={<Organizations />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/eula" element={<Eula />} />
                    <Route path="/schedule-a" element={<ScheduleA />} />
                    <Route path="/schedule-b" element={<ScheduleB />} />
                    <Route path="/providers" element={<Providers />} />
                    <Route path="/security" element={<SecurityPage />} />
                    <Route
                      path={paths.publicAuth.forgotPassword}
                      element={<ForgotPasswordPage />}
                    />
                    <Route
                      path={`${paths.publicAuth.ssoCallback}/*`}
                      element={<SSOCallbackPage />}
                    />
                    <Route
                      path={paths.sessionTasks.chooseOrganization}
                      element={<SessionTaskPage task="choose-organization" />}
                    />
                    <Route
                      path={paths.sessionTasks.resetPassword}
                      element={<SessionTaskPage task="reset-password" />}
                    />
                    <Route
                      path={paths.sessionTasks.setupMfa}
                      element={<SessionTaskPage task="setup-mfa" />}
                    />
                    <Route path="*" element={<UnauthenticatedFallback />} />
                  </Routes>
                  <SiteFooter />
                </div>
              </Show>
              <Show when="signed-in">
                <ObservabilityIdentityBridge />
                <SignedInShell>
                  <FeatureFlagProvider>
                    <AuthenticatedRoutes />
                  </FeatureFlagProvider>
                </SignedInShell>
              </Show>
            </>
          )}
        </UserContext.Provider>
      </div>
    </>
  );
};

export default App;
